'use client';

import React, { type ReactNode, useEffect, useRef } from 'react';

import { sendMetricIncrement } from '@/app/components/hotel-detail/metrics';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { isPricingStale } from '@/app/components/hotel-detail/common/pricingFreshness';
import { useCreateAsyncPricing } from './PricingProvider';

type SessionProviderProps = {
  children: ReactNode;
};

export const SessionContext = React.createContext<undefined>(undefined);

export default function SessionProvider(props: SessionProviderProps) {
  const { children } = props;
  const { pageType } = useInitialParams();
  const { mutate, data: asyncPricing, isValidating } = useCreateAsyncPricing();
  const pricingTime = asyncPricing?.time;
  const requestedForTime = useRef<number | undefined>(undefined);

  function newSessionFromUser() {
    mutate();
    sendMetricIncrement(`app.session.${pageType}.sessionRenewal`);
  }

  useEffect(() => {
    /**
     * Checks for successful log in and log out events
     * To ask for a new session with updated prices
     */
    window.addEventListener('lmn:login:successful', newSessionFromUser);
    window.addEventListener('lmn:logout:successful', newSessionFromUser);

    return () => {
      window.removeEventListener('lmn:login:successful', newSessionFromUser);
      window.removeEventListener('lmn:logout:successful', newSessionFromUser);
    };
  }, []);

  // MPH-785: the 29-minute refreshInterval is the only thing keeping the ~30-minute pricing alive
  // (see PricingProvider), but SWR skips its tick while the tab is hidden and reschedules it a full
  // interval later — so a backgrounded page routinely comes back with an expired pricing, and every
  // request that then uses it (rooms, cart creation) fails with not-found errors. Refresh it the
  // moment the tab becomes visible again, mirroring the login/logout renewal above.
  useEffect(() => {
    async function renewStalePricingOnVisible() {
      if (document.visibilityState !== 'visible' || !isPricingStale(pricingTime)) return;
      // One request per attempt: a refresh may already be in flight (the review-entry refresh, the
      // 29-minute tick — mutate() bypasses SWR's deduping marker, so nothing else prevents a second
      // pricing-id request), and the ref blocks the stale-closure window this listener has of its
      // own — a second visibilitychange can fire before the isValidating render reaches us, and the
      // ref is synchronous where the prop is not.
      if (isValidating || requestedForTime.current === pricingTime) return;
      requestedForTime.current = pricingTime;
      sendMetricIncrement(`app.pricing.${pageType}.stale-pricing-refresh.visibility`);
      // A refresh that LANDS moves the timestamp and the staleness check stands down on its own — the
      // ref never blocks anything again. One that FAILS leaves the pricing expired with nothing else
      // coming: SWR stops the keep-alive tick while an error sits in the key's cache, and on the
      // detail step there is no review-entry hook and no modal to take over. So re-arm, and the
      // user's most natural gesture — switching away and back — becomes the retry. The bound mutate
      // resolves with the post-revalidation pricing and never rejects.
      const refreshed = await mutate();
      if (refreshed?.time === undefined || refreshed.time === pricingTime) {
        requestedForTime.current = undefined;
      }
    }

    document.addEventListener('visibilitychange', renewStalePricingOnVisible);
    return () => document.removeEventListener('visibilitychange', renewStalePricingOnVisible);
    // mutate and pageType are stable; the listener only needs re-binding when the pricing changes or a
    // refresh settles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingTime, isValidating]);

  return <SessionContext.Provider value={undefined}>{children}</SessionContext.Provider>;
}
